import Button from '@/shared/Button'

import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'

import CloseIcon from '@mui/icons-material/Close'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import FileDownloadDoneIcon from '@mui/icons-material/FileDownloadDone'
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt'

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import CsvUpload from './CsvUpload'
import Stepper from '../Stepper'

import set from 'lodash/set'
import ColumnMapping from './ColumnMapping'
import UploadPreview from './UploadPreview'
import Spinner from '@/shared/Spinner'

const mapStateToProps = ({ loading }) => ({
  isUploading: loading['userGroup/uploadFile'],
  isChecking: loading['userGroup/checkFile'],
  isFinalizing: loading['userGroup/uploadFileFinal'],
})

const columnTitleMapping = {
  email: 'Email',
  phone: 'Phone',
  cname: '中文名字',
  companyName: '公司名稱',
  firstName: '名',
  lastName: '姓',
  department: '部門',
  deptGroup: '事業群',
  office: '辦公室',
  position: '職位',
  manager: '主管',
  joinOn: '到職日期',
}

const UploadListDialog = ({
  dispatch,
  isUploading,
  isChecking,
  isFinalizing,
  onClose,
  onComplete,
  data,
  userGroupName,
  ...props
}) => {
  const csvFileRef = useRef()
  const [step, setStep] = useState(0)
  const [stepNext, setStepNext] = useState([false, true, true])
  const [simulateItem, setSimulateItem] = useState('EMAIL')
  const [uploadResponse, setUploadResponse] = useState({})
  const [fileCheckResponse, setFileCheckResponse] = useState({})
  const [mappingData, setMappingData] = useState({})
  const [userGroup, setUserGroup] = useState()
  const [checkingUpload, setCheckingUpload] = useState(false)

  const { t } = useTranslation()

  useEffect(() => {
    const newMappingData = {}

    uploadResponse?.titleList?.forEach(title => {
      const mapTarget = uploadResponse?.uploadTitleListl.find(target => {
        const a = target.replace(/\s/g, '').replaceAll('"', '').toLowerCase()
        const b = title.toLowerCase()
        const c = columnTitleMapping[title]

        return a === b || a === c
      })

      if (mapTarget) {
        newMappingData[title] = mapTarget
      }
    })

    switch (simulateItem) {
      case 'EMAIL':
        if (!newMappingData.email) {
          newMappingData.email = ''
        }

        break
      case 'PHONE':
        if (!newMappingData.phone) {
          newMappingData.phone = ''
        }

        break
      case 'EMAILANDPHONE':
        if (!newMappingData.email) {
          newMappingData.email = ''
        }

        if (!newMappingData.phone) {
          newMappingData.phone = ''
        }
        break
      default:
        break
    }

    setMappingData(newMappingData)
  }, [uploadResponse, simulateItem])

  const handleClose = () => {
    onClose?.()
  }

  const resetState = () => {
    csvFileRef.current = null
    setStep(0)
    setStepNext([false, true, true])
    setUploadResponse({})
    setMappingData({})
    setUserGroup()
  }

  const isLoading = isUploading || isChecking || isFinalizing || checkingUpload

  const checkUpload = uuid => {
    dispatch({
      type: 'userGroup/checkUploadStatus',
      payload: { uuid },
      successCallback: ({ uploadStatus, totalUpload }) => {
        switch (uploadStatus) {
          case 'COM':
            setCheckingUpload(false)

            onComplete?.()
            handleClose?.()
            resetState()

            dispatch({
              type: 'app/notify',
              payload: {
                message: t('messages.upload_success_user_list', { count: totalUpload }),
                severity: 'success',
                sx: { backgroundColor: theme => theme.palette.primary.main },
                duration: null,
              },
            })
            break
          case 'IP':
            setTimeout(() => {
              checkUpload(uuid)
            }, 2000)
            break
          default:
            setCheckingUpload(false)
            dispatch({
              type: 'app/notify',
              payload: {
                message: t('messages.user_group_file_fail'),
                severity: 'error',
              },
            })
            break
        }
      },
      errorCallback: message => {
        dispatch({
          type: 'app/notify',
          payload: {
            message,
            severity: 'error',
          },
        })
      },
    })
  }

  const steps = [
    {
      label: t('general.select_upload_file'),
      icon: <SystemUpdateAltIcon sx={{ transform: 'rotate(180deg)' }} />,
    },
    { label: t('general.select_data_order'), icon: <DescriptionOutlinedIcon /> },
    { label: t('general.confirm_data_order'), icon: <FileDownloadDoneIcon /> },
  ]

  const setStepNextByIndex = (index, val) => {
    const newStepNext = [...stepNext]
    set(newStepNext, `[${index}]`, val)
    setStepNext(newStepNext)
  }

  const onNext = () => {
    switch (step) {
      case 0: {
        const formData = new FormData()
        formData.append('file', csvFileRef.current)

        dispatch({
          type: 'userGroup/uploadFile',
          payload: formData,
          params: { phishType: simulateItem },
          successCallback: response => {
            setUploadResponse(response)
            setStep(step + 1)
          },
        })
        break
      }
      case 1: {
        const { tempUUID } = uploadResponse

        dispatch({
          type: 'userGroup/checkFile',
          payload: { tempUUID, titleColumnMap: mappingData },
          successCallback: res => {
            setFileCheckResponse(res)
            setStepNextByIndex(step + 1, true)
            setStep(step + 1)
          },
        })
        break
      }
      case 2: {
        const { tempUUID } = uploadResponse

        setCheckingUpload(true)

        dispatch({
          type: 'userGroup/uploadFileFinal',
          payload: { tempUUID, groupName: userGroup || '' },
        })

        checkUpload(tempUUID)
        break
      }
      default:
        break
    }
  }

  const renderStepComponent = () => {
    switch (step) {
      case 0:
        return (
          <>
            <Typography sx={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: '12px' }}>
              {t('general.select_upload_csv_description_part_one')}
              <br />
              {t('general.select_upload_csv_description_part_two')}
            </Typography>
            <CsvUpload
              dispatch={dispatch}
              onFileChange={file => {
                csvFileRef.current = file
                setStepNextByIndex(step, !!file)
              }}
              value={csvFileRef.current}
              {...{ simulateItem, setSimulateItem }}
            />
          </>
        )
      case 1:
        return (
          <>
            <Typography sx={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: '12px' }}>
              {t('general.select_upload_csv_fields')}
            </Typography>
            <ColumnMapping
              data={uploadResponse}
              setMappingData={setMappingData}
              mappingData={mappingData}
              simulateItem={simulateItem}
            />
          </>
        )
      case 2: {
        const { tempUUID } = uploadResponse

        return (
          <>
            <Typography sx={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: '12px' }}>
              {t('general.confirm_upload_csv_fields')}
            </Typography>
            <UploadPreview
              uuid={tempUUID}
              fileCheckResponse={fileCheckResponse}
              onError={() => {
                setStepNextByIndex(step, false)
              }}
              setUserGroup={d => {
                setUserGroup(d)
              }}
              userGroup={userGroup}
              userGroupName={userGroupName}
              columnData={uploadResponse}
            />
          </>
        )
      }
      default:
        return null
    }
  }

  return (
    <Dialog
      {...props}
      maxWidth={'md'}
      sx={{
        '& .MuiDialog-paper': {
          padding: '24px 48px',
          maxWidth: 'unset',
        },
      }}
    >
      <DialogTitle>
        <Stepper step={step} steps={steps} />
        <IconButton
          edge="end"
          color="#979797"
          onClick={() => {
            resetState()
            handleClose()
          }}
          aria-label="close"
          sx={{
            position: 'absolute',
            right: '24px',
            top: '12px',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ maxWidth: { md: 800, lg: 'unset' }, width: 1000 }}>
        {renderStepComponent()}
      </DialogContent>
      <DialogActions>
        <Box sx={{ marginLeft: 'auto' }}>
          <Button
            sx={{ minWidth: '150px', marginRight: '30px' }}
            loading={isLoading}
            disabled={isLoading}
            onClick={() => {
              if (step === 0) {
                handleClose?.()
                resetState()
              } else {
                setStep(step - 1)
              }
            }}
          >
            {step !== 0 ? t('general.prev_step') : t('general.cancel')}
          </Button>
          <Button
            sx={{ minWidth: '150px' }}
            loading={isLoading}
            disabled={isLoading || !stepNext[step]}
            onClick={onNext}
          >
            {step !== steps.length - 1 ? t('general.next_step') : t('general.confirm_upload')}
          </Button>
        </Box>
      </DialogActions>
      {isLoading && (
        <Dialog open>
          <DialogContent>
            <Stack spacing={2} padding={3}>
              <Spinner />
              <Typography>{t('messages.loading')}</Typography>
            </Stack>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  )
}

export default connect(mapStateToProps)(UploadListDialog)
